import { translate } from "app/filters/i18nFilter";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { columnKeys } from "app/pages/sales-intelligence/utils/i18n/columns";

import {
    PRE_DEFINED_CREATE_FIELD,
    PRE_DEFINED_FIELD,
    PRE_DEFINED_UPDATE_FIELD,
    TECHNOGRAPHICS_FIELD,
} from "app/pages/sales-intelligence/common-components/export-table/const";
import type {
    AccountsFieldKey,
    SimilarwebFieldsConfig,
} from "app/pages/sales-intelligence/common-components/export-table/types";

import { getOpportunityListFieldsConfig } from "./opportunityListFields";
import { getRequesterFieldsConfig } from "./requesterFields";
import { getSubsidiaryFieldsConfig } from "./subsidiaryFields";

import {
    PREDEFINED_CREATE_FIELD_TOOLTIP_KEY,
    PREDEFINED_FIELD_TOOLTIP_KEY,
    PREDEFINED_UPDATE_FIELD_TOOLTIP_KEY,
} from "app/pages/sales-intelligence/common-components/export-table/constants";

export const preDefinedField: Readonly<SimilarwebFieldsConfig> = Object.seal({
    [`${PRE_DEFINED_FIELD}_modal`]: {
        get name() {
            return translate("si.management.integration.export.pre_defined.field.dropdown");
        },
        example: "E.g. Created by similarweb",
        field: PRE_DEFINED_FIELD,
        tooltipKey: PREDEFINED_FIELD_TOOLTIP_KEY,
    },
});

export const preDefinedCreateField: Readonly<SimilarwebFieldsConfig> = Object.seal({
    [`${PRE_DEFINED_CREATE_FIELD}_modal`]: {
        get name() {
            return translate("si.management.integration.export.pre_defined_create.field.dropdown");
        },
        example: "E.g. Created by similarweb",
        field: PRE_DEFINED_CREATE_FIELD,
        tooltipKey: PREDEFINED_CREATE_FIELD_TOOLTIP_KEY,
    },
});

export const preDefinedUpdateField: Readonly<SimilarwebFieldsConfig> = Object.seal({
    [`${PRE_DEFINED_UPDATE_FIELD}_modal`]: {
        get name() {
            return translate("si.management.integration.export.pre_defined_update.field.dropdown");
        },
        example: "E.g. Updated by similarweb",
        field: PRE_DEFINED_UPDATE_FIELD,
        tooltipKey: PREDEFINED_UPDATE_FIELD_TOOLTIP_KEY,
    },
});

export const technographicsField: Readonly<SimilarwebFieldsConfig> = Object.seal({
    [`${TECHNOGRAPHICS_FIELD}_modal`]: {
        get name() {
            return translate(columnKeys.title("technographics"));
        },
        example: "(E.g. Shopify, Magento)",
        field: TECHNOGRAPHICS_FIELD,
    },
});

export const consolidatedAppsFieldsConfig = (): SimilarwebFieldsConfig<AccountsFieldKey> => {
    return Object.seal({
        ["apps_rating"]: {
            name: translate(columnKeys.title("apps_rating")),
            example: "4.5",
            field: "apps_rating",
        },
        ["apps_monthly_downloads"]: {
            name: translate(columnKeys.title("apps_monthly_downloads")),
            example: "1,234,567",
            field: "apps_monthly_downloads",
        },
        ["apps_monthly_active_users"]: {
            name: translate(columnKeys.title("apps_monthly_active_users")),
            example: "5,678,901",
            field: "apps_monthly_active_users",
        },
    });
};

export const adSpendFieldsConfig = (): SimilarwebFieldsConfig<AccountsFieldKey> => {
    return Object.seal({
        ["total_ad_spend"]: {
            name: translate(columnKeys.title("total_ad_spend")),
            example: "15,234,567",
            field: "total_ad_spend",
        },
        ["total_ad_spend_change_mom"]: {
            name: translate(columnKeys.title("total_ad_spend_change_mom")),
            example: "3.25%",
            field: "total_ad_spend_change_mom",
        },
        ["search_ad_spend"]: {
            name: translate(columnKeys.title("search_ad_spend")),
            example: "8,456,789",
            field: "search_ad_spend",
        },
        ["search_ad_spend_change_mom"]: {
            name: translate(columnKeys.title("search_ad_spend_change_mom")),
            example: "2.15%",
            field: "search_ad_spend_change_mom",
        },
        ["display_ad_spend"]: {
            name: translate(columnKeys.title("display_ad_spend")),
            example: "6,777,778",
            field: "display_ad_spend",
        },
        ["display_ad_spend_change_mom"]: {
            name: translate(columnKeys.title("display_ad_spend_change_mom")),
            example: "4.82%",
            field: "display_ad_spend_change_mom",
        },
        ["total_ad_active_campaigns"]: {
            name: translate(columnKeys.title("total_ad_active_campaigns")),
            example: "142",
            field: "total_ad_active_campaigns",
        },
        ["search_ad_active_campaigns"]: {
            name: translate(columnKeys.title("search_ad_active_campaigns")),
            example: "67",
            field: "search_ad_active_campaigns",
        },
        ["display_ad_active_campaigns"]: {
            name: translate(columnKeys.title("display_ad_active_campaigns")),
            example: "75",
            field: "display_ad_active_campaigns",
        },
        ["total_ad_number"]: {
            name: translate(columnKeys.title("total_ad_number")),
            example: "2,458",
            field: "total_ad_number",
        },
        ["search_ad_number"]: {
            name: translate(columnKeys.title("search_ad_number")),
            example: "1,234",
            field: "search_ad_number",
        },
        ["display_ad_number"]: {
            name: translate(columnKeys.title("display_ad_number")),
            example: "1,224",
            field: "display_ad_number",
        },
        ["display_ads_mobile_share"]: {
            name: translate(columnKeys.title("display_ads_mobile_share")),
            example: "62.45%",
            field: "display_ads_mobile_share",
        },
        ["display_ads_desktop_share"]: {
            name: translate(columnKeys.title("display_ads_desktop_share")),
            example: "37.55%",
            field: "display_ads_desktop_share",
        },
        ["display_ads_banner_share"]: {
            name: translate(columnKeys.title("display_ads_banner_share")),
            example: "45.20%",
            field: "display_ads_banner_share",
        },
        ["display_ads_video_share"]: {
            name: translate(columnKeys.title("display_ads_video_share")),
            example: "38.50%",
            field: "display_ads_video_share",
        },
        ["display_ads_native_share"]: {
            name: translate(columnKeys.title("display_ads_native_share")),
            example: "16.30%",
            field: "display_ads_native_share",
        },
        ["search_channel_share"]: {
            name: translate(columnKeys.title("search_channel_share")),
            example: "55.48%",
            field: "search_channel_share",
        },
        ["display_channel_share"]: {
            name: translate(columnKeys.title("display_channel_share")),
            example: "44.52%",
            field: "display_channel_share",
        },
        ["total_ads_mobile_share"]: {
            name: translate(columnKeys.title("total_ads_mobile_share")),
            example: "58.75%",
            field: "total_ads_mobile_share",
        },
        ["total_ads_desktop_share"]: {
            name: translate(columnKeys.title("total_ads_desktop_share")),
            example: "41.25%",
            field: "total_ads_desktop_share",
        },
        ["search_ads_mobile_share"]: {
            name: translate(columnKeys.title("search_ads_mobile_share")),
            example: "54.30%",
            field: "search_ads_mobile_share",
        },
        ["search_ads_desktop_share"]: {
            name: translate(columnKeys.title("search_ads_desktop_share")),
            example: "45.70%",
            field: "search_ads_desktop_share",
        },
    });
};

export const INDUSTRY_TAXONOMY_FIELD_KEYS = [
    "industry_taxonomy",
    "industry_sector",
    "industry_vertical",
    "industry_primary_offering",
] as const;

export const getIndustryTaxonomyFieldsConfig = (): SimilarwebFieldsConfig<AccountsFieldKey> =>
    Object.seal({
        industry_taxonomy: {
            name: translate(columnKeys.title("industry_taxonomy")),
            example: "Media & Entertainment/Video Content & Streaming/Broadcast News Media",
            field: "industry_taxonomy",
            badgeType: "new",
        },
        industry_sector: {
            name: translate(columnKeys.title("industry_sector")),
            example: "Media & Entertainment",
            field: "industry_sector",
            badgeType: "new",
        },
        industry_vertical: {
            name: translate(columnKeys.title("industry_vertical")),
            example: "Video Content & Streaming",
            field: "industry_vertical",
            badgeType: "new",
        },
        industry_primary_offering: {
            name: translate(columnKeys.title("industry_primary_offering")),
            example: "Broadcast News Media",
            field: "industry_primary_offering",
            badgeType: "new",
        },
    });

export const prioritizeIndustryTaxonomyFields = <T extends { field: string }>(fields: readonly T[]): T[] => {
    const industryFieldKeys = new Set<string>(INDUSTRY_TAXONOMY_FIELD_KEYS);
    const fieldsByKey = new Map(fields.map((field) => [field.field, field]));

    return [
        ...INDUSTRY_TAXONOMY_FIELD_KEYS.flatMap((key) => {
            const field = fieldsByKey.get(key);
            return field ? [field] : [];
        }),
        ...fields.filter((field) => !industryFieldKeys.has(field.field)),
    ];
};

type SimilarwebFieldDropdownOptionsParams<T extends { field: string }> = {
    readonly fields: readonly T[];
    readonly predefinedItems: readonly T[];
    readonly techographicsItem?: T;
};

export const buildSimilarwebFieldDropdownOptions = <T extends { field: string }>({
    fields,
    predefinedItems,
    techographicsItem,
}: SimilarwebFieldDropdownOptionsParams<T>): T[] => {
    const industryFieldKeys = new Set<string>(INDUSTRY_TAXONOMY_FIELD_KEYS);
    const prioritizedFields = prioritizeIndustryTaxonomyFields(fields);
    const industryTaxonomyFields = prioritizedFields.filter((field) => industryFieldKeys.has(field.field));
    const otherFields = prioritizedFields.filter((field) => !industryFieldKeys.has(field.field));

    return [
        ...industryTaxonomyFields,
        ...predefinedItems,
        ...(techographicsItem ? [techographicsItem] : []),
        ...otherFields,
    ];
};

const SCORING_FIELD_KEYS = ["lead_score", "lead_score_ai_summary", "lead_score_last_update_date"] as const;

export const getScoringFieldKeys = (): readonly string[] => SCORING_FIELD_KEYS;

export const getScoringFieldsConfig = (): SimilarwebFieldsConfig<AccountsFieldKey> =>
    Object.seal({
        ["lead_score"]: {
            name: translate(columnKeys.title("lead_score")),
            example: "High",
            field: "lead_score",
            badgeType: "new",
            isEnrichmentOnly: true,
        },
        ["lead_score_ai_summary"]: {
            name: translate(columnKeys.title("lead_score_ai_summary")),
            example: "Google is rapidly shipping Gem...",
            field: "lead_score_ai_summary",
            badgeType: "new",
            isEnrichmentOnly: true,
        },
        ["lead_score_last_update_date"]: {
            name: translate(columnKeys.title("lead_score_last_update_date")),
            example: "10:37 AM Jul 1, 2026",
            field: "lead_score_last_update_date",
            badgeType: "new",
            isEnrichmentOnly: true,
        },
    });

export const getAccountsSourceFieldsConfig = (): SimilarwebFieldsConfig<AccountsFieldKey> => {
    const baseConfig: SimilarwebFieldsConfig<AccountsFieldKey> = {
        ...getIndustryTaxonomyFieldsConfig(),
        site: {
            name: translate(columnKeys.title("website")),
            example: "acme-labs.com",
            field: "site",
        },
        ...getScoringFieldsConfig(),
        rank: {
            name: translate(columnKeys.title("rank")),
            example: "147",
            field: "rank",
        },
        industry: {
            name: translate(columnKeys.title("industry")),
            example: "Arts & Entertainment/TV & Video",
            field: "industry",
        },
        visits: {
            name: translate(columnKeys.title("visits")),
            example: "252,454,392",
            field: "visits",
        },
        ["avg_monthly_estimated_visits"]: {
            name: translate(columnKeys.title("avg_monthly_estimated_visits")),
            example: "203,102,825",
            field: "avg_monthly_estimated_visits",
        },
        ["monthly_visits_change_mom"]: {
            name: translate(columnKeys.title("monthly_visits_change_mom")),
            example: "-1.59%",
            field: "monthly_visits_change_mom",
        },
        ["monthly_visits_change_yoy"]: {
            name: translate(columnKeys.title("monthly_visits_change_yoy")),
            example: "0.84%",
            field: "monthly_visits_change_yoy",
        },
        visitors: {
            name: translate(columnKeys.title("visitors")),
            example: "19,731,712",
            field: "visitors",
        },
        ["unique_monthly_visitors_change_mom"]: {
            name: translate(columnKeys.title("unique_monthly_visitors_change_mom")),
            example: "-4.25%",
            field: "unique_monthly_visitors_change_mom",
        },
        ["unique_monthly_visitors_change_yoy"]: {
            name: translate(columnKeys.title("unique_monthly_visitors_change_yoy")),
            example: "1.96%",
            field: "unique_monthly_visitors_change_yoy",
        },
        ["desktop_visits_share"]: {
            name: translate(columnKeys.title("desktop_visits_share")),
            example: "41.59%",
            field: "desktop_visits_share",
        },
        ["mobileweb_visits_share"]: {
            name: translate(columnKeys.title("mobileweb_visits_share")),
            example: "58.41%",
            field: "mobileweb_visits_share",
        },
        ["avg_visit_duration"]: {
            name: translate(columnKeys.title("avg_visit_duration")),
            example: "00:11:24",
            field: "avg_visit_duration",
        },
        ["pages_per_visit"]: {
            name: translate(columnKeys.title("pages_per_visit")),
            example: "8.69",
            field: "pages_per_visit",
        },
        ["avg_visits_per_user"]: {
            name: translate(columnKeys.title("avg_visits_per_user")),
            example: "1.09",
            field: "avg_visits_per_user",
        },
        pageviews: {
            name: translate(columnKeys.title("pageviews")),
            example: "2,192,715,381",
            field: "pageviews",
        },
        ["total_page_views_change_mom"]: {
            name: translate(columnKeys.title("total_page_views_change_mom")),
            example: "-1.15%",
            field: "total_page_views_change_mom",
        },
        ["total_page_views_change_yoy"]: {
            name: translate(columnKeys.title("total_page_views_change_yoy")),
            example: "2.52%",
            field: "total_page_views_change_yoy",
        },
        ["bounce_rate"]: {
            name: translate(columnKeys.title("bounce_rate")),
            example: "27.83%",
            field: "bounce_rate",
        },
        ["desktop_visits"]: {
            name: translate(columnKeys.title("desktop_visits")),
            example: "104,995,782",
            field: "desktop_visits",
        },
        ["desktop_monthly_visits_change_mom"]: {
            name: translate(columnKeys.title("desktop_monthly_visits_change_mom")),
            example: "-0.78%",
            field: "desktop_monthly_visits_change_mom",
        },
        ["desktop_monthly_visits_change_yoy"]: {
            name: translate(columnKeys.title("desktop_monthly_visits_change_yoy")),
            example: "-0.29%",
            field: "desktop_monthly_visits_change_yoy",
        },
        ["desktop_visitors"]: {
            name: translate(columnKeys.title("desktop_visitors")),
            example: "8,206,419",
            field: "desktop_visitors",
        },
        ["desktop_unique_monthly_visitors_change_mom"]: {
            name: translate(columnKeys.title("desktop_unique_monthly_visitors_change_mom")),
            example: "-8.47%",
            field: "desktop_unique_monthly_visitors_change_mom",
        },
        ["desktop_unique_monthly_visitors_change_yoy"]: {
            name: translate(columnKeys.title("desktop_unique_monthly_visitors_change_yoy")),
            example: "3.85%",
            field: "desktop_unique_monthly_visitors_change_yoy",
        },
        ["desktop_avg_visit_duration"]: {
            name: translate(columnKeys.title("desktop_avg_visit_duration")),
            example: "00:17:21",
            field: "desktop_avg_visit_duration",
        },
        ["desktop_pages_per_visit"]: {
            name: translate(columnKeys.title("desktop_pages_per_visit")),
            example: "12.07",
            field: "desktop_pages_per_visit",
        },
        ["desktop_pageviews"]: {
            name: translate(columnKeys.title("desktop_pageviews")),
            example: "1,267,517,744",
            field: "desktop_pageviews",
        },
        ["desktop_total_page_views_change_mom"]: {
            name: translate(columnKeys.title("desktop_total_page_views_change_mom")),
            example: "-0.40%",
            field: "desktop_total_page_views_change_mom",
        },
        ["desktop_total_page_views_change_yoy"]: {
            name: translate(columnKeys.title("desktop_total_page_views_change_yoy")),
            example: "2.16%",
            field: "desktop_total_page_views_change_yoy",
        },
        ["desktop_bounce_rate"]: {
            name: translate(columnKeys.title("desktop_bounce_rate")),
            example: "23.58%",
            field: "desktop_bounce_rate",
        },
        ["mobileweb_visits"]: {
            name: translate(columnKeys.title("mobileweb_visits")),
            example: "147,458,610",
            field: "mobileweb_visits",
        },
        ["mobile_monthly_visits_change_mom"]: {
            name: translate(columnKeys.title("mobile_monthly_visits_change_mom")),
            example: "-2.15%",
            field: "mobile_monthly_visits_change_mom",
        },
        ["mobile_monthly_visits_change_yoy"]: {
            name: translate(columnKeys.title("mobile_monthly_visits_change_yoy")),
            example: "1.66%",
            field: "mobile_monthly_visits_change_yoy",
        },
        ["mobileweb_visitors"]: {
            name: translate(columnKeys.title("mobileweb_visitors")),
            example: "11,525,293",
            field: "mobileweb_visitors",
        },
        ["mobile_unique_monthly_visitors_change_mom"]: {
            name: translate(columnKeys.title("mobile_unique_monthly_visitors_change_mom")),
            example: "-2.91%",
            field: "mobile_unique_monthly_visitors_change_mom",
        },
        ["mobile_unique_monthly_visitors_change_yoy"]: {
            name: translate(columnKeys.title("mobile_unique_monthly_visitors_change_yoy")),
            example: "1.40%",
            field: "mobile_unique_monthly_visitors_change_yoy",
        },
        ["mobileweb_avg_visit_duration"]: {
            name: translate(columnKeys.title("mobileweb_avg_visit_duration")),
            example: "00:07:09",
            field: "mobileweb_avg_visit_duration",
        },
        ["mobileweb_pages_per_visit"]: {
            name: translate(columnKeys.title("mobileweb_pages_per_visit")),
            example: "6.27",
            field: "mobileweb_pages_per_visit",
        },
        ["mobileweb_pageviews"]: {
            name: translate(columnKeys.title("mobileweb_pageviews")),
            example: "925,143,042",
            field: "mobileweb_pageviews",
        },
        ["mobile_total_page_views_change_mom"]: {
            name: translate(columnKeys.title("mobile_total_page_views_change_mom")),
            example: "-2.15%",
            field: "mobile_total_page_views_change_mom",
        },
        ["mobile_total_page_views_change_yoy"]: {
            name: translate(columnKeys.title("mobile_total_page_views_change_yoy")),
            example: "3.01%",
            field: "mobile_total_page_views_change_yoy",
        },
        ["mobileweb_bounce_rate"]: {
            name: translate(columnKeys.title("mobileweb_bounce_rate")),
            example: "30.86%",
            field: "mobileweb_bounce_rate",
        },
        ["direct_visits_share"]: {
            name: translate(columnKeys.title("direct_visits_share")),
            example: "74.08%",
            field: "direct_visits_share",
        },
        ["mail_visits_share"]: {
            name: translate(columnKeys.title("mail_visits_share")),
            example: "2.22%",
            field: "mail_visits_share",
        },
        ["referrals_visits_share"]: {
            name: translate(columnKeys.title("referrals_visits_share")),
            example: "2.58%",
            field: "referrals_visits_share",
        },
        ["social_visits_share"]: {
            name: translate(columnKeys.title("social_visits_share")),
            example: "2.53%",
            field: "social_visits_share",
        },
        ["organic_search_visits_share"]: {
            name: translate(columnKeys.title("organic_search_visits_share")),
            example: "0.47%",
            field: "organic_search_visits_share",
        },
        ["paid_search_visits_share"]: {
            name: translate(columnKeys.title("paid_search_visits_share")),
            example: "7.05%",
            field: "paid_search_visits_share",
        },
        ["displayads_visits_share"]: {
            name: translate(columnKeys.title("displayads_visits_share")),
            example: "11.08%",
            field: "displayads_visits_share",
        },
        ["conversion_rate"]: {
            name: translate(columnKeys.title("conversion_rate")),
            example: "3.25%",
            field: "conversion_rate",
        },
        ["conversion_rate_change_mom"]: {
            name: translate(columnKeys.title("conversion_rate_change_mom")),
            example: "-0.42%",
            field: "conversion_rate_change_mom",
        },
        ["conversion_rate_change_yoy"]: {
            name: translate(columnKeys.title("conversion_rate_change_yoy")),
            example: "1.15%",
            field: "conversion_rate_change_yoy",
        },
        ["direct_visits"]: {
            name: translate(columnKeys.title("direct_visits")),
            example: "187,012,628",
            field: "direct_visits",
        },
        ["direct_change_mom"]: {
            name: translate(columnKeys.title("direct_visits_change_mom")),
            example: "-0.82%",
            field: "direct_change_mom",
        },
        ["direct_change_yoy"]: {
            name: translate(columnKeys.title("direct_visits_change_yoy")),
            example: "-1.01%",
            field: "direct_change_yoy",
        },
        ["mail_visits"]: {
            name: translate(columnKeys.title("mail_visits")),
            example: "5,609,223",
            field: "mail_visits",
        },
        ["mail_change_mom"]: {
            name: translate(columnKeys.title("mail_visits_change_mom")),
            example: "0.63%",
            field: "mail_change_mom",
        },
        ["mail_change_yoy"]: {
            name: translate(columnKeys.title("mail_visits_change_yoy")),
            example: "-13.00%",
            field: "mail_change_yoy",
        },
        ["referrals_visits"]: {
            name: translate(columnKeys.title("referrals_visits")),
            example: "6,515,586",
            field: "referrals_visits",
        },
        ["referrals_change_mom"]: {
            name: translate(columnKeys.title("referrals_visits_change_mom")),
            example: "1.16%",
            field: "referrals_change_mom",
        },
        ["referrals_change_yoy"]: {
            name: translate(columnKeys.title("referrals_visits_change_yoy")),
            example: "28.89%",
            field: "referrals_change_yoy",
        },
        ["social_visits"]: {
            name: translate(columnKeys.title("social_visits")),
            example: "6,380,730",
            field: "social_visits",
        },
        ["social_change_mom"]: {
            name: translate(columnKeys.title("social_visits_change_mom")),
            example: "-1.83%",
            field: "social_change_mom",
        },
        ["social_change_yoy"]: {
            name: translate(columnKeys.title("social_visits_change_yoy")),
            example: "1.72%",
            field: "social_change_yoy",
        },
        ["organic_search_visits"]: {
            name: translate(columnKeys.title("organic_search_visits")),
            example: "1,183,259",
            field: "organic_search_visits",
        },
        ["organic_search_change_mom"]: {
            name: translate(columnKeys.title("organic_search_visits_change_mom")),
            example: "-0.30%",
            field: "organic_search_change_mom",
        },
        ["organic_search_change_yoy"]: {
            name: translate(columnKeys.title("organic_search_visits_change_yoy")),
            example: "-8.59%",
            field: "organic_search_change_yoy",
        },
        ["usd_spend"]: {
            get name() {
                return translate("si.lead_gen_filters.group.usdSpend.name");
            },
            example: "11,691,003",
            field: "usd_spend",
        },
        ["paid_search_visits"]: {
            name: translate(columnKeys.title("paid_search_visits")),
            example: "17,787,968",
            field: "paid_search_visits",
        },
        ["paid_search_change_mom"]: {
            name: translate(columnKeys.title("paid_search_visits_change_mom")),
            example: "-2.20%",
            field: "paid_search_change_mom",
        },
        ["paid_search_change_yoy"]: {
            name: translate(columnKeys.title("paid_search_visits_change_yoy")),
            example: "5.09%",
            field: "paid_search_change_yoy",
        },
        ["displayads_visits"]: {
            name: translate(columnKeys.title("displayads_visits")),
            example: "27,964,998",
            field: "displayads_visits",
        },
        ["displayads_change_mom"]: {
            name: translate(columnKeys.title("display_ads_visits_change_mom")),
            example: "-0.65%",
            field: "displayads_change_mom",
        },
        ["displayads_change_yoy"]: {
            name: translate(columnKeys.title("display_ads_visits_change_yoy")),
            example: "67.20%",
            field: "displayads_change_yoy",
        },
        ["organic_social_visits"]: {
            name: translate(columnKeys.title("organic_social_visits")),
            example: "3,190,365",
            field: "organic_social_visits",
        },
        ["organic_social_visits_share"]: {
            name: translate(columnKeys.title("organic_social_visits_share")),
            example: "1.26%",
            field: "organic_social_visits_share",
        },
        ["organic_social_change_mom"]: {
            name: translate(columnKeys.title("organic_social_visits_change_mom")),
            example: "-0.95%",
            field: "organic_social_change_mom",
        },
        ["organic_social_change_yoy"]: {
            name: translate(columnKeys.title("organic_social_visits_change_yoy")),
            example: "2.14%",
            field: "organic_social_change_yoy",
        },
        ["paid_social_visits"]: {
            name: translate(columnKeys.title("paid_social_visits")),
            example: "3,190,365",
            field: "paid_social_visits",
        },
        ["paid_social_visits_share"]: {
            name: translate(columnKeys.title("paid_social_visits_share")),
            example: "1.27%",
            field: "paid_social_visits_share",
        },
        ["paid_social_change_mom"]: {
            name: translate(columnKeys.title("paid_social_visits_change_mom")),
            example: "-2.50%",
            field: "paid_social_change_mom",
        },
        ["paid_social_change_yoy"]: {
            name: translate(columnKeys.title("paid_social_visits_change_yoy")),
            example: "3.80%",
            field: "paid_social_change_yoy",
        },
        ["gen_ai_visits"]: {
            name: translate(columnKeys.title("gen_ai_visits")),
            example: "1,045,230",
            field: "gen_ai_visits",
        },
        ["gen_ai_visits_share"]: {
            name: translate(columnKeys.title("gen_ai_visits_share")),
            example: "0.41%",
            field: "gen_ai_visits_share",
        },
        ["gen_ai_change_mom"]: {
            name: translate(columnKeys.title("gen_ai_visits_change_mom")),
            example: "12.30%",
            field: "gen_ai_change_mom",
        },
        ["gen_ai_change_yoy"]: {
            name: translate(columnKeys.title("gen_ai_visits_change_yoy")),
            example: "145.50%",
            field: "gen_ai_change_yoy",
        },
        ["affiliate_visits"]: {
            name: translate(columnKeys.title("affiliate_visits")),
            example: "2,580,120",
            field: "affiliate_visits",
        },
        ["affiliate_visits_share"]: {
            name: translate(columnKeys.title("affiliate_visits_share")),
            example: "1.02%",
            field: "affiliate_visits_share",
        },
        ["affiliate_change_mom"]: {
            name: translate(columnKeys.title("affiliate_visits_change_mom")),
            example: "0.75%",
            field: "affiliate_change_mom",
        },
        ["affiliate_change_yoy"]: {
            name: translate(columnKeys.title("affiliate_visits_change_yoy")),
            example: "5.20%",
            field: "affiliate_change_yoy",
        },
        ["search_visits"]: {
            name: translate(columnKeys.title("search_visits")),
            example: "18,971,227",
            field: "search_visits",
        },
        ["search_visits_share"]: {
            name: translate(columnKeys.title("search_visits_share")),
            example: "7.52%",
            field: "search_visits_share",
        },
        ["search_change_mom"]: {
            name: translate(columnKeys.title("search_visits_change_mom")),
            example: "-1.50%",
            field: "search_change_mom",
        },
        ["search_change_yoy"]: {
            name: translate(columnKeys.title("search_visits_change_yoy")),
            example: "3.20%",
            field: "search_change_yoy",
        },
        ["company_name"]: {
            name: translate(columnKeys.title("company_name")),
            example: "ACME Corp",
            field: "company_name",
        },
        ["company_description"]: {
            name: translate(columnKeys.title("company_description")),
            example: "ACME Corporation is a leading innovator in gadgetry",
            field: "company_description",
        },
        ["company_founding_year"]: {
            name: translate(columnKeys.title("company_founding_year")),
            example: "1962",
            field: "company_founding_year",
        },
        ["business_model"]: {
            name: translate(columnKeys.title("business_model")),
            example: "B2B",
            field: "business_model",
        },
        ["site_tags"]: {
            name: translate(columnKeys.title("site_tags")),
            example: "Web Development",
            field: "site_tags",
        },
        ["company_full_hq"]: {
            name: translate(columnKeys.title("company_full_hq")),
            example: "35 E 21st St, New York, New York 10010, United States",
            field: "company_full_hq",
        },
        ["company_address"]: {
            name: translate(columnKeys.title("company_address")),
            example: "35 E 21st St",
            field: "company_address",
        },
        ["company_city"]: {
            name: translate(columnKeys.title("company_city")),
            example: "New York",
            field: "company_city",
        },
        ["company_state"]: {
            name: translate(columnKeys.title("company_state")),
            example: "New York",
            field: "company_state",
        },
        ["company_country"]: {
            name: translate(columnKeys.title("company_country")),
            example: "United States",
            field: "company_country",
        },
        ["company_zip"]: {
            name: translate(columnKeys.title("company_zip")),
            example: "10010",
            field: "company_zip",
        },
        ["siren_number"]: {
            name: translate(columnKeys.title("siren_number")),
            example: "552 100 554",
            field: "siren_number",
        },
        ["australian_business_number"]: {
            name: translate(columnKeys.title("australian_business_number")),
            example: "51 824 753 556",
            field: "australian_business_number",
        },
        ["company_employee_range"]: {
            name: translate(columnKeys.title("company_employee_range")),
            example: "500- 1,000",
            field: "company_employee_range",
        },
        ["company_online_revenue_range"]: {
            name: translate(columnKeys.title("company_online_revenue_range")),
            example: "0 - 10M",
            field: "company_online_revenue_range",
        },
        ["company_revenue_range"]: {
            name: translate(columnKeys.title("company_revenue_range")),
            example: "100M - 200M",
            field: "company_revenue_range",
        },
        ["company_phone"]: {
            name: translate(columnKeys.title("company_phone")),
            example: "1-800-540-1086",
            field: "company_phone",
        },
        ["company_email"]: {
            name: translate(columnKeys.title("company_email")),
            example: "acme@acmecorpo.com",
            field: "company_email",
        },
        ["male_share"]: {
            name: translate(columnKeys.title("male_share")),
            example: "57%",
            field: "male_share",
        },
        ["female_share"]: {
            name: translate(columnKeys.title("female_share")),
            example: "43%",
            field: "female_share",
        },
        ["age_group_18_24_share"]: {
            name: translate(columnKeys.title("age_group_18_24_share")),
            example: "24.20%",
            field: "age_group_18_24_share",
        },
        ["age_group_25_34_share"]: {
            name: translate(columnKeys.title("age_group_25_34_share")),
            example: "33.77%",
            field: "age_group_25_34_share",
        },
        ["age_group_35_44_share"]: {
            name: translate(columnKeys.title("age_group_35_44_share")),
            example: "19.35%",
            field: "age_group_35_44_share",
        },
        ["age_group_45_54_share"]: {
            name: translate(columnKeys.title("age_group_45_54_share")),
            example: "11.39%",
            field: "age_group_45_54_share",
        },
        ["age_group_55_64_share"]: {
            name: translate(columnKeys.title("age_group_55_64_share")),
            example: "7.24%",
            field: "age_group_55_64_share",
        },
        ["age_group_65_share"]: {
            name: translate(columnKeys.title("age_group_65_share")),
            example: "4.05%",
            field: "age_group_65_share",
        },
        ["international_visits"]: {
            name: translate(columnKeys.title("international_visits")),
            example: "252,454,392",
            field: "international_visits",
        },
        ["international_visits_share"]: {
            name: translate(columnKeys.title("international_visits_share")),
            example: "38.41%",
            field: "international_visits_share",
        },
        ["linkedin_url"]: {
            name: translate(columnKeys.title("linkedin_url")),
            example: "https://www.linkedin.com/company/acme-labs",
            field: "linkedin_url",
        },
        ["company_country_alpha2"]: {
            name: translate(columnKeys.title("company_country_alpha2")),
            example: "US",
            field: "company_country_alpha2",
        },
        ["site_types"]: {
            name: translate(columnKeys.title("site_types")),
            example: "ecommerce, advertiser",
            field: "site_types",
        },
        ["top_geo_country"]: {
            name: translate(columnKeys.title("top_geo_country")),
            example: "United States",
            field: "top_geo_country",
        },
        ["monthly_avg_transactions_range"]: {
            name: translate(columnKeys.title("monthly_avg_transactions_range")),
            example: "10K - 100K",
            field: "monthly_avg_transactions_range",
        },
        ["company_apps"]: {
            name: translate(columnKeys.title("company_apps")),
            example: "Google Maps, Waze",
            field: "company_apps",
        },
    };

    return Object.seal({
        ...baseConfig,
        ...(salesSettingsHelper.isSIAdIntelligenceEnabled ? adSpendFieldsConfig() : {}),
        ...(salesSettingsHelper.isConsolidatedAppsEnabled ? consolidatedAppsFieldsConfig() : {}),
    });
};

export const getAccountsFields = (): SimilarwebFieldsConfig<AccountsFieldKey> =>
    Object.seal({
        ...getAccountsSourceFieldsConfig(),
        ...getRequesterFieldsConfig(),
        ...getSubsidiaryFieldsConfig(),
        ...getOpportunityListFieldsConfig(),
    });
